import { useEffect, useState } from "react";
import { FormSection, HelpList, Icon, More, formatNumber } from "@omnabase/ui";
import { investigationEffortSummary, type Project, type WorkspaceSettings } from "@omnabase/shared";
import { api } from "../../lib/api";
import { useCan } from "../../lib/access";
import { useNav, useWs } from "../../lib/nav";
import { useFormState, useSaver } from "../../lib/save";
import { useSession } from "../../shell/store";

// Settings › AI › Instructions (AI settings round one, 2026-10-18): the company-wide instructions (read by the prompts from M5 round two),
// "Projects with their own settings" as the kit's table (project, drafts, instructions, effort, a link to the project's AI tab), and what the
// AI reads (the knowledge base, every source's items since the cleanup round). A project's own instructions and effort stay on its AI tab.

const INSTRUCTION_EXAMPLES = [
  "Answer in the customer's language; sign off with the first name only.",
  "Keep it short: three sentences, then a link to the article.",
  "Never promise a release date or a refund; say you will pass it on.",
  "Call the product \"Enfold\", never \"the theme\".",
];
type SourceLine = { items: number };
const SECTIONS = ["tone", "facts", "policies", "escalation"] as const;

export function AiInstructions() {
  return <><CompanyWide /><ProjectsTable /><WhatTheAiReads /></>;
}

function CompanyWide() {
  const { workspace, reload, toast } = useSession();
  const can = useCan()("aiSettings");
  const [w, setW] = useFormState<WorkspaceSettings>(workspace.settings, workspace.id); // the form is the source of truth while open (lib/save.ts)
  const { save } = useSaver<Partial<WorkspaceSettings>>(async (patch) => { await api.patch(`/api/v1/w/${workspace.slug}/settings`, patch); await reload(); }, toast);
  const change = (v: string) => { setW((cur) => ({ ...cur, aiInstructions: v })); save({ aiInstructions: v }); };
  return (
    <FormSection title="Company-wide instructions" audience="locked" intro={<>Applied before every project&apos;s own instructions. Tone, languages, what never to promise.<More label="Examples"><HelpList items={INSTRUCTION_EXAMPLES} /></More></>}>
      <textarea className="ob-instr__text" value={w.aiInstructions} onChange={(e) => change(e.target.value)} aria-label="Company-wide instructions" readOnly={!can} maxLength={4000} />
      <p className="ob-form-row__help">Each project adds its own under Settings › Projects › AI.</p>
    </FormSection>
  );
}

/** what a project sets itself: its drafts switch, its own instructions (any of the four sections written), its own investigation effort */
const ownInstructions = (p: Project) => SECTIONS.some((k) => (p.instructions?.[k] ?? "").trim() !== "");
const differs = (p: Project) => p.ai?.drafts !== undefined || p.ai?.investigationEffort !== undefined || ownInstructions(p);

function ProjectsTable() {
  const nav = useNav(); const { ws } = useWs();
  const { workspace } = useSession();
  const rows = workspace.projects.filter((p) => !p.archivedAt && differs(p));
  return (
    <FormSection title="Projects with their own settings" audience="admins" intro="These projects set something themselves; the rest follow the workspace.">
      {rows.length === 0 ? <p className="ob-form-row__help">Every project follows the workspace.</p> : (
        <div className="ob-dtable-card">
          <table className="ob-dtable" aria-label="Projects with their own settings">
            <thead><tr><th>Project</th><th className="ob-dtable__col--below-540">AI drafts</th><th>Instructions</th><th className="ob-dtable__col--below-680">Effort</th><th></th></tr></thead>
            <tbody>{rows.map((p) => { const to = ws(`/settings/projects/${p.id}?tab=ai`); return (
              <tr key={p.id}>
                <td className="ob-dtable__grow"><a className="ob-dtable__name" href={to} onClick={(e) => nav(to, e)}>{p.name}</a></td>
                <td className="ob-dtable__muted ob-dtable__col--below-540">{p.ai?.drafts === undefined ? "Workspace's" : p.ai.drafts ? "On" : "Off"}</td>
                <td className="ob-dtable__muted">{ownInstructions(p) ? "Own" : "Company-wide"}</td>
                <td className="ob-dtable__muted ob-dtable__col--below-680">{p.ai?.investigationEffort === undefined ? "Workspace's" : investigationEffortSummary(p.ai.investigationEffort)}</td>
                <td className="ob-dtable__actions"><a className="ob-link" href={to} onClick={(e) => nav(to, e)} aria-label={`${p.name}'s AI tab`}><Icon name="chevronRight" size="sm" /></a></td>
              </tr>); })}</tbody>
          </table>
        </div>
      )}
    </FormSection>
  );
}

/** the knowledge base and how much of it the AI reads (every source feeds the AI, the cleanup round, 2026-10-12) */
function WhatTheAiReads() {
  const nav = useNav(); const { ws } = useWs();
  const { workspace } = useSession();
  const [reads, setReads] = useState<{ sources: number; items: number } | null>(null);
  const projectIds = workspace.projects.filter((p) => !p.archivedAt).map((p) => p.id).join(",");
  useEffect(() => {
    let alive = true;
    const ids = [...projectIds.split(",").filter(Boolean), "shared"];
    Promise.all(ids.map((id) => api.cached<{ sources?: SourceLine[] }>(`/api/v1/w/${workspace.slug}/projects/${id}/knowledge`).catch(() => ({ sources: [] as SourceLine[] }))))
      .then((all) => { if (!alive) return; const sources = all.flatMap((o) => o.sources ?? []); setReads({ sources: sources.length, items: sources.reduce((n, s) => n + s.items, 0) }); });
    return () => { alive = false; };
  }, [workspace.slug, projectIds]);
  const knowledge = ws("/knowledge");
  return (
    <FormSection title="What the AI reads" audience="admins" intro="Drafts cite sources from the knowledge base. Anything not covered lands in Needs review.">
      <a className="ob-form__link-row" href={knowledge} onClick={(e) => nav(knowledge, e)}>
        <Icon name="book" size="sm" /><span>Knowledge base</span>
        <span className="ob-form__link-row-meta">{reads ? `${reads.sources} sources · ${formatNumber(reads.items)} items readable by the AI` : ""}</span>
        <Icon name="chevronRight" size="sm" />
      </a>
    </FormSection>
  );
}
